import React from 'react';
import cx from 'classnames';
import { Link } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { GetDataError } from 'restful-react';

import {
	useDropdown,
	useAppContext,
	useTrackEvent,
	useMemberContext,
} from 'core/hooks';
import { Icon, ICONS } from 'core/components/Icon';
import {
	setAccountSwitchToolTip,
	getAccountSwitchToolTip,
} from 'core/browserStorage';
import {
	formatCurrency,
	getProductClassificationLabel,
} from 'core/utils/global-utils';
import * as Typo from 'core/styles/Typography';

import { MenuItem } from '../../Header.sitecore';
import * as S from './DesktopHeader.style';

export interface BalanceProps {
	productClassification: string;
	balance: number | undefined;
	loading: boolean;
	error: GetDataError<Error> | null;
}

interface IProps {
	menuItem: MenuItem;
	dataTargetRoot?: string;
	isActiveNav: boolean;
	accountSwitcherIcon: ICONS;
	balanceData?: BalanceProps;
}

const DesktopNavItem = ({
	menuItem,
	dataTargetRoot,
	isActiveNav,
	accountSwitcherIcon,
	balanceData,
}: IProps) => {
	const ref = React.useRef<HTMLLIElement>();
	const toolTipRef = React.useRef<HTMLElement>();
	const { isExpanded, toggleDropdown, Dropdown } = useDropdown(ref);
	const { id, navigationTitle, url, children } = menuItem;
	const {
		handleAccountSwitcherModal,
		isAccountSplashModalHidden,
	} = useAppContext();
	const { accounts = [] } = useMemberContext();

	React.useEffect(() => {
		if (getAccountSwitchToolTip() !== 'true') {
			const toolTipRefCurrent = toolTipRef.current;
			const removeInlineVisibility = () => {
				if (toolTipRefCurrent) {
					toolTipRefCurrent.style.visibility = '';
				}
				setAccountSwitchToolTip('true');
			};

			window.addEventListener('scroll', removeInlineVisibility);

			if (isAccountSplashModalHidden && toolTipRefCurrent) {
				toolTipRefCurrent.style.visibility = 'visible';
				setTimeout(() => {
					removeInlineVisibility();
				}, 5000);
			}

			return () => {
				window.removeEventListener('scroll', removeInlineVisibility);
			};
		}
	}, [isAccountSplashModalHidden]);

	// Analytics spreadsheet:
	// - Section: 'Header'
	// - ID: 1.1.2
	const { trackEvent } = useTrackEvent(
		{
			event_section: 'navigation',
			event_type: 'header',
			event_title: 'authenticated',
		},
		false,
	);

	/**
	 * Track the clicking event and toggle the dropdown off.
	 */
	const handleNavLinkClick = React.useCallback(
		(subItem: MenuItem | null = null) => () => {
			// Analytics spreadsheet:
			// - Section: 'Header'
			// - ID: 1.1.2
			trackEvent({
				event_section: 'navigation',
				event_type: 'header',
				event_title: 'authenticated',
				event_label: `${navigationTitle.value.toLowerCase()}${
					subItem ? ` - ${subItem.navigationTitle.value.toLowerCase()}` : ''
				}`,
				event_trigger: 'click link',
				event_url: subItem ? subItem.url : menuItem.url,
			});

			toggleDropdown();
		},
		[navigationTitle, toggleDropdown, menuItem.url, trackEvent],
	);

	const renderAccountSwitcher = () => {
		const isMultipleAccount = accounts?.length > 1;

		return (
			<>
				<S.AccountSwitcherLink
					arrowIconTop={isMultipleAccount}
					onClick={() => {
						if (accounts?.length === 1) {
							return false;
						}

						handleAccountSwitcherModal('open');
						// Analytics spreadsheet:
						// - Section: 'Header'
						// - ID: 1.1.3
						trackEvent({
							event_trigger: 'click link',
							event_label: `account type - ${getProductClassificationLabel(
								balanceData?.productClassification,
							)}`,
						});
					}}
				>
					<S.AccountSwitcherContentContainer showIcon={isMultipleAccount}>
						<S.SwitchIcon
							name={accountSwitcherIcon ?? ICONS.SWITCH_CLOSED}
							size={24}
							className="switch-icon-hover"
						/>
						<Typo.SubHeading
							tag="p"
							data-target-id="desktop-nav--account-switcher--label"
						>
							<S.ProductClassification>
								{getProductClassificationLabel(
									balanceData?.productClassification,
								)}
							</S.ProductClassification>

							<S.AccountBalance>
								{(balanceData?.loading && balanceData?.error && (
									<S.CenterContents>
										<Icon
											name={ICONS.MORE_HORIZ}
											size={20}
											className="account-balance-loader"
										/>
									</S.CenterContents>
								)) ||
									`(${formatCurrency(balanceData?.balance ?? 0)})`}
							</S.AccountBalance>
						</Typo.SubHeading>
					</S.AccountSwitcherContentContainer>
					<S.ArrowNavImage name={ICONS.ACCOUNT_ARROW_NAV} />
				</S.AccountSwitcherLink>
				{/* NOTE: This can be converted to Sitecore Field */}
				<S.AccountSwitcherTooltip ref={toolTipRef as any}>
					{'You can switch accounts here whenever you need'}
				</S.AccountSwitcherTooltip>
			</>
		);
	};

	return (
		<li
			ref={ref as any}
			className={cx('desktop-nav--item', { '-active': isActiveNav })}
			aria-haspopup={isExpanded}
			role="menuitem"
		>
			{!isEmpty(children) ? (
				<>
					<S.NavButton
						onClick={toggleDropdown}
						type="button"
						data-target-id={`${dataTargetRoot}--level1-expand-btn-${id}`}
						aria-pressed={isExpanded}
					>
						{navigationTitle.value}
					</S.NavButton>
					<Dropdown
						topOffset={62}
						alignment="plain-right-justified"
						isShowTriangleOnTop={false}
					>
						<S.SubNavList isExpanded={isExpanded} aria-hidden={isExpanded}>
							{children!.map(subItem => (
								<li key={subItem.id} aria-haspopup={false}>
									<Link
										to={subItem.url}
										onClick={handleNavLinkClick(subItem)}
										data-target-id={`${dataTargetRoot}--level2-link-${subItem.id}`}
									>
										{subItem.navigationTitle.value}
									</Link>
								</li>
							))}
						</S.SubNavList>
					</Dropdown>
				</>
			) : id === 'account-switcher' ? (
				renderAccountSwitcher()
			) : (
				<Link
					to={url}
					data-target-id={`${dataTargetRoot}--level1-link-${id}`}
					onClick={handleNavLinkClick()}
				>
					{navigationTitle.value}
				</Link>
			)}
		</li>
	);
};

export default React.memo(DesktopNavItem);
